import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useNavigate } from "react-router-dom";
import { useGetAllCustomersQuery, useDeleteUserMutation } from '../../services/userCRUDApi';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './Customers.css';

function Customer() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isSmallScreen ? 5 : 10);
  const [success, setSuccess] = useState(false);
  const { data, isLoading } = useGetAllCustomersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const navigate = useNavigate();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id) => {
    navigate(`/admin/customer/edit/${id}`);
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: 'Confirm to Delete Customer',
      message: 'Are you sure you want to delete this customer?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await deleteUser(id).unwrap();
              setSuccess(true);
              setTimeout(() => setSuccess(false), 3000);
            } catch (error) {
              console.error("Error deleting user:", error);
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

const inactiveCustomers = data ? data.filter(customer => customer.is_active === false) : [];
const paginatedCustomers = inactiveCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="customers-container">
      {/* Header Section */}
      <Box className="title-section">
        <Typography variant="h5" className="user-title">
          Customers List
        </Typography>
        <Box className="alert-and-button">
          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess(false)}
              className="success-alert"
              sx={{ mb: isSmallScreen ? 1 : 0 }}
            >
              Customer deleted successfully
            </Alert>
          )}

        </Box>
      </Box>

      {isSmallScreen ? (
        /* Mobile Card View */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {paginatedCustomers.length > 0 ? (
            paginatedCustomers.map((customer) => (
              <Card key={customer.id} className="customer-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {customer.first_Name} {customer.last_Name}
                    </Typography>
                    <Chip
                      label={`ID: ${customer.id}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <Box component="span" fontWeight="bold">Email:</Box> {customer.email}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <Box component="span" fontWeight="bold">Phone:</Box> {customer.phone_Number}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={() => handleDelete(customer.id)}
                      color="error"
                      fullWidth
                    >
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body1" textAlign="center" py={4}>
              No customers found
            </Typography>
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0d47a1',
                borderRadius: '3px'
              }
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: '#0d47a1' }}>
                <TableRow>
                  <TableCell sx={{ color: '#fff', minWidth: '50px',backgroundColor: '#0d47a1' }}>ID</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '120px',backgroundColor: '#0d47a1' }}>First Name</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '120px',backgroundColor: '#0d47a1' }}>Last Name</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '180px' ,backgroundColor: '#0d47a1'}}>Email</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '120px' ,backgroundColor: '#0d47a1'}}>Phone</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '100px' ,backgroundColor: '#0d47a1'}}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedCustomers.length > 0 ? (
                  paginatedCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>{customer.first_Name}</TableCell>
                      <TableCell>{customer.last_Name}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{customer.email}</TableCell>
                      <TableCell>{customer.phone_Number}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(customer.id)}
                          aria-label="edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(customer.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No customers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <TablePagination
                      component="div"
                      count={inactiveCustomers.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25]}
                      sx={{
                        backgroundColor: '#0d47a1',
                        color: '#fff',
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          color: '#fff',
                          fontSize: '0.875rem',
                          my: 0
                        },
                        '& .MuiSelect-icon': {
                          color: '#fff',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiInputBase-root': {
                          color: '#fff',
                        },
                        '& .MuiTablePagination-actions': {
                          marginLeft: 0
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Mobile Pagination */}
      {isSmallScreen && paginatedCustomers.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
}

export default Customer;